import { useState, useEffect, useRef } from 'preact/hooks';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { 
  Save,
  Undo,
  Redo,
  Copy,
  Paste,
  Search,
  Replace,
  Settings,
  Maximize,
  Minimize,
  X,
  FileText,
  Code,
  Palette,
  Type,
  Zap
} from 'lucide-preact';

interface ProjectFile {
  name: string;
  type: 'file' | 'directory';
  path: string;
  size?: number;
  modified?: number;
  children?: ProjectFile[];
  projectId?: string;
}

interface CodeEditorProps {
  file?: ProjectFile;
  onSave: (filePath: string, content: string) => Promise<{ success: boolean; message?: string; error?: string }>;
  onClose: () => void;
  isFullscreen?: boolean;
  onToggleFullscreen?: () => void;
}

interface EditorTab {
  id: string;
  file: ProjectFile;
  isActive: boolean;
  isDirty: boolean;
}

export function CodeEditor({
  file,
  onSave,
  onClose,
  isFullscreen = false,
  onToggleFullscreen
}: CodeEditorProps) {
  const editorRef = useRef<HTMLDivElement>(null);
  const monacoRef = useRef<any>(null);
  const [editor, setEditor] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [replaceQuery, setReplaceQuery] = useState('');
  const [showSearch, setShowSearch] = useState(false);
  const [showReplace, setShowReplace] = useState(false);
  const [tabs, setTabs] = useState<EditorTab[]>([]);
  const [activeTab, setActiveTab] = useState<string | null>(null);
  const [theme, setTheme] = useState<'vs-dark' | 'vs-light' | 'hc-black'>('vs-dark');
  const [fontSize, setFontSize] = useState(14);
  const [wordWrap, setWordWrap] = useState<'on' | 'off' | 'wordWrapColumn' | 'bounded'>('on');

  useEffect(() => {
    initializeMonaco();
    return () => {
      if (editor) {
        editor.dispose();
      }
    };
  }, []);

  useEffect(() => {
    if (file && editor) {
      loadFile(file);
    }
  }, [file, editor]);

  // Add keyboard shortcut support
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        handleSave();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleSave]);

  const initializeMonaco = async () => {
    if (typeof window === 'undefined') return;

    try {
      // Dynamically import Monaco Editor
      const monaco = await import('monaco-editor');
      monacoRef.current = monaco;

      // Configure Monaco
      monaco.editor.defineTheme('claude-dark', {
        base: 'vs-dark',
        inherit: true,
        rules: [
          { token: 'comment', foreground: '6A9955' },
          { token: 'keyword', foreground: '569CD6' },
          { token: 'string', foreground: 'CE9178' },
          { token: 'number', foreground: 'B5CEA8' },
          { token: 'regexp', foreground: 'D16969' },
          { token: 'type', foreground: '4EC9B0' },
          { token: 'class', foreground: '4EC9B0' },
          { token: 'interface', foreground: '4EC9B0' },
          { token: 'function', foreground: 'DCDCAA' },
          { token: 'variable', foreground: '9CDCFE' },
        ],
        colors: {
          'editor.background': '#1e1e1e',
          'editor.foreground': '#d4d4d4',
          'editorLineNumber.foreground': '#858585',
          'editorLineNumber.activeForeground': '#c6c6c6',
          'editor.selectionBackground': '#264f78',
          'editor.selectionHighlightBackground': '#add6ff26',
          'editorCursor.foreground': '#aeafad',
          'editorWhitespace.foreground': '#e3e4e229',
          'editorIndentGuide.background': '#404040',
          'editorIndentGuide.activeBackground': '#707070',
        }
      });

      monaco.editor.setTheme('claude-dark');

      // Create editor instance
      const editorInstance = monaco.editor.create(editorRef.current!, {
        value: '',
        language: 'typescript',
        theme: 'claude-dark',
        fontSize: fontSize,
        wordWrap: wordWrap,
        minimap: { enabled: true },
        scrollBeyondLastLine: false,
        automaticLayout: true,
        tabSize: 2,
        insertSpaces: true,
        renderWhitespace: 'selection',
        renderControlCharacters: true,
        fontLigatures: true,
        fontFamily: 'Fira Code, Monaco, Consolas, monospace',
        cursorBlinking: 'blink',
        cursorSmoothCaretAnimation: true,
        smoothScrolling: true,
        mouseWheelZoom: true,
        contextmenu: true,
        selectOnLineNumbers: true,
        roundedSelection: false,
        readOnly: false,
        folding: true,
        foldingStrategy: 'indentation',
        showFoldingControls: 'always',
        unfoldOnClickAfterEnd: false,
        bracketPairColorization: { enabled: true },
        guides: {
          bracketPairs: true,
          indentation: true,
        },
        suggest: {
          showKeywords: true,
          showSnippets: true,
          showFunctions: true,
          showConstructors: true,
          showFields: true,
          showVariables: true,
          showClasses: true,
          showStructs: true,
          showInterfaces: true,
          showModules: true,
          showProperties: true,
          showEvents: true,
          showOperators: true,
          showUnits: true,
          showValues: true,
          showConstants: true,
          showEnums: true,
          showEnumMembers: true,
          showColors: true,
          showFiles: true,
          showReferences: true,
          showFolders: true,
          showTypeParameters: true,
        },
        quickSuggestions: {
          other: true,
          comments: false,
          strings: true,
        },
        suggestOnTriggerCharacters: true,
        acceptSuggestionOnEnter: 'on',
        tabCompletion: 'on',
        wordBasedSuggestions: 'matchingDocuments',
        parameterHints: { enabled: true },
        hover: { enabled: true },
        lightbulb: { enabled: true },
        codeLens: true,
        formatOnPaste: true,
        formatOnType: true,
        autoClosingBrackets: 'languageDefined',
        autoClosingQuotes: 'languageDefined',
        autoSurround: 'languageDefined',
        autoIndent: 'advanced',
        dragAndDrop: true,
        links: true,
        colorDecorators: true,
        codeActionsOnSave: {
          'source.fixAll': true,
          'source.organizeImports': true,
        },
      });

      setEditor(editorInstance);

      // Add event listeners
      editorInstance.onDidChangeModelContent(() => {
        setHasUnsavedChanges(true);
      });

      editorInstance.onDidChangeCursorPosition((e) => {
        // Handle cursor position changes
      });

      editorInstance.onDidFocusEditorText(() => {
        // Handle editor focus
      });

      // Add keyboard shortcuts
      editorInstance.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyS, () => {
        handleSave();
      });

      editorInstance.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyF, () => {
        setShowSearch(true);
      });

      editorInstance.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyMod.Shift | monaco.KeyCode.KeyF, () => {
        setShowReplace(true);
      });

      editorInstance.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyZ, () => {
        editorInstance.trigger('', 'undo', null);
      });

      editorInstance.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyMod.Shift | monaco.KeyCode.KeyZ, () => {
        editorInstance.trigger('', 'redo', null);
      });

    } catch (error) {
      console.error('Failed to initialize Monaco Editor:', error);
    }
  };

  const loadFile = async (file: ProjectFile) => {
    if (!editor || !file) return;

    setIsLoading(true);
    try {
      // Set the language based on file extension
      const language = getLanguageFromExtension(file.path);
      const model = editor.getModel();
      
      if (model) {
        monacoRef.current.editor.setModelLanguage(model, language);
      }

      // Load file content from API
      let content = '';
      if (file.type === 'file') {
        try {
          // Extract project ID from the file path or use a global project ID
          // For now, we'll need to pass projectId as a prop
          const response = await fetch(`/api/projects/${file.projectId || 'current'}/files/${file.path}`);
          if (response.ok) {
            const data = await response.json();
            content = data.content || '';
          }
        } catch (error) {
          console.error('Failed to load file content:', error);
          content = '';
        }
      }
      
      editor.setValue(content);

      // Add or update tab
      const existingTab = tabs.find(tab => tab.file.path === file.path);
      if (existingTab) {
        setActiveTab(file.path);
      } else {
        const newTab: EditorTab = {
          id: file.path, // Use path as ID
          file: file,
          isActive: true,
          isDirty: false
        };
        setTabs(prev => [...prev.map(t => ({ ...t, isActive: false })), newTab]);
        setActiveTab(file.path);
      }

      setHasUnsavedChanges(false);
    } catch (error) {
      console.error('Failed to load file:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getLanguageFromExtension = (path: string): string => {
    const extension = path.split('.').pop()?.toLowerCase();
    
    const languageMap: { [key: string]: string } = {
      'js': 'javascript',
      'jsx': 'javascript',
      'ts': 'typescript',
      'tsx': 'typescript',
      'py': 'python',
      'java': 'java',
      'cpp': 'cpp',
      'c': 'c',
      'cs': 'csharp',
      'php': 'php',
      'rb': 'ruby',
      'go': 'go',
      'rs': 'rust',
      'html': 'html',
      'css': 'css',
      'scss': 'scss',
      'sass': 'sass',
      'less': 'less',
      'json': 'json',
      'xml': 'xml',
      'yaml': 'yaml',
      'yml': 'yaml',
      'md': 'markdown',
      'txt': 'plaintext',
      'log': 'plaintext',
      'csv': 'csv',
      'sql': 'sql',
      'sh': 'shell',
      'bash': 'shell',
      'dockerfile': 'dockerfile',
      'gitignore': 'ignore',
      'env': 'properties',
    };

    return languageMap[extension || ''] || 'plaintext';
  };

  const handleSave = async () => {
    if (!editor || !file || !hasUnsavedChanges) return;

    setIsSaving(true);
    try {
      const content = editor.getValue();
      const result = await onSave(file.path, content);
      
      if (result.success) {
        setHasUnsavedChanges(false);
        
        // Update tab dirty state
        setTabs(prev => prev.map(tab => 
          tab.file.path === file.path ? { ...tab, isDirty: false } : tab
        ));
        
        console.log('File saved successfully:', result.message);
      } else {
        console.error('Failed to save file:', result.error);
        // You could show a toast notification here
      }
    } catch (error) {
      console.error('Failed to save file:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleSearch = () => {
    if (!editor) return;
    
    const model = editor.getModel();
    if (model) {
      editor.getAction('actions.find')?.run();
    }
  };

  const handleReplace = () => {
    if (!editor) return;
    
    const model = editor.getModel();
    if (model) {
      editor.getAction('editor.action.startFindReplaceAction')?.run();
    }
  };

  const handleUndo = () => {
    if (editor) {
      editor.trigger('', 'undo', null);
    }
  };

  const handleRedo = () => {
    if (editor) {
      editor.trigger('', 'redo', null);
    }
  };

  const handleFormat = () => {
    if (editor) {
      editor.getAction('editor.action.formatDocument')?.run();
    }
  };

  const handleCloseTab = (tabId: string) => {
    setTabs(prev => {
      const newTabs = prev.filter(tab => tab.id !== tabId);
      if (tabId === activeTab && newTabs.length > 0) {
        setActiveTab(newTabs[0].id);
      } else if (newTabs.length === 0) {
        setActiveTab(null);
        onClose();
      }
      return newTabs;
    });
  };

  const handleTabClick = (tabId: string) => {
    setActiveTab(tabId);
    const tab = tabs.find(t => t.id === tabId);
    if (tab) {
      loadFile(tab.file);
    }
  };

  const renderTabs = () => (
    <div className="flex border-b bg-muted/20">
      {tabs.map(tab => (
        <div
          key={tab.id}
          className={`flex items-center gap-2 px-3 py-2 cursor-pointer border-r ${
            tab.id === activeTab 
              ? 'bg-background border-b-2 border-b-primary' 
              : 'hover:bg-muted/50'
          }`}
          onClick={() => handleTabClick(tab.id)}
        >
          <FileText className="h-4 w-4" />
          <span className="text-sm truncate max-w-32">{tab.file.path}</span>
          {tab.isDirty && <div className="w-2 h-2 rounded-full bg-orange-500" />}
          <Button
            variant="ghost"
            size="sm"
            className="h-4 w-4 p-0 hover:bg-destructive/10"
            onClick={(e) => {
              e.stopPropagation();
              handleCloseTab(tab.id);
            }}
          >
            <X className="h-3 w-3" />
          </Button>
        </div>
      ))}
    </div>
  );

  const renderToolbar = () => (
    <div className="flex items-center gap-2 p-2 border-b bg-muted/20">
      <div className="flex items-center gap-1">
        <Button
          variant="ghost"
          size="sm"
          onClick={handleSave}
          disabled={!hasUnsavedChanges || isSaving}
          className="h-8"
        >
          <Save className="h-4 w-4 mr-1" />
          {isSaving ? 'Saving...' : 'Save'}
        </Button>
        
        <Button variant="ghost" size="sm" onClick={handleUndo} className="h-8">
          <Undo className="h-4 w-4" />
        </Button>
        
        <Button variant="ghost" size="sm" onClick={handleRedo} className="h-8">
          <Redo className="h-4 w-4" />
        </Button>
      </div>

      <div className="flex items-center gap-1 ml-4">
        <Button variant="ghost" size="sm" onClick={handleSearch} className="h-8">
          <Search className="h-4 w-4" />
        </Button>
        
        <Button variant="ghost" size="sm" onClick={handleReplace} className="h-8">
          <Replace className="h-4 w-4" />
        </Button>
        
        <Button variant="ghost" size="sm" onClick={handleFormat} className="h-8">
          <Code className="h-4 w-4" />
        </Button>
      </div>

      <div className="flex items-center gap-1 ml-auto">
        <select
          value={theme}
          onChange={(e) => setTheme((e.target as HTMLSelectElement).value as any)}
          className="text-xs px-2 py-1 border rounded bg-background"
        >
          <option value="vs-dark">Dark</option>
          <option value="vs-light">Light</option>
          <option value="hc-black">High Contrast</option>
        </select>
        
        <select
          value={fontSize}
          onChange={(e) => setFontSize(parseInt((e.target as HTMLSelectElement).value))}
          className="text-xs px-2 py-1 border rounded bg-background"
        >
          <option value="12">12px</option>
          <option value="14">14px</option>
          <option value="16">16px</option>
          <option value="18">18px</option>
          <option value="20">20px</option>
        </select>
        
        {onToggleFullscreen && (
          <Button variant="ghost" size="sm" onClick={onToggleFullscreen} className="h-8">
            {isFullscreen ? <Minimize className="h-4 w-4" /> : <Maximize className="h-4 w-4" />}
          </Button>
        )}
      </div>
    </div>
  );

  if (!file) {
    return (
      <Card className="flex-1">
        <CardContent className="flex items-center justify-center h-full">
          <div className="text-center text-muted-foreground">
            <FileText className="h-12 w-12 mx-auto mb-4" />
            <p>No file selected</p>
            <p className="text-sm">Open a file from the file explorer to start editing</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className={`flex flex-col h-full ${isFullscreen ? 'fixed inset-0 z-50 bg-background' : ''}`}>
      {renderTabs()}
      {renderToolbar()}
      
      <div className="flex-1 relative">
        <div
          ref={editorRef}
          className="w-full h-full"
          style={{ minHeight: '400px' }}
        />
        
        {isLoading && (
          <div className="absolute inset-0 bg-background/50 flex items-center justify-center">
            <div className="flex items-center gap-2">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
              <span className="text-sm">Loading...</span>
            </div>
          </div>
        )}
      </div>

      {/* Status Bar */}
      <div className="flex items-center justify-between px-4 py-2 border-t bg-muted/20 text-xs text-muted-foreground">
        <div className="flex items-center gap-4">
          <span>{file.path}</span>
          <span>{getLanguageFromExtension(file.path)}</span>
          {hasUnsavedChanges && <Badge variant="outline" className="text-orange-600">Unsaved</Badge>}
        </div>
        
        <div className="flex items-center gap-4">
          <span>Ln 1, Col 1</span>
          <span>UTF-8</span>
          <span>Spaces: 2</span>
        </div>
      </div>
    </div>
  );
}
